import dayjs from 'dayjs';
import type { FieldValidator } from 'final-form';
import { t } from 'i18next';
import { postcodeValidator } from 'postcode-validator';

import type { Country } from '../features/geography/types';

export const enum FieldType {
  email = 'Email',
  password = 'Password',
  confirmPassword = 'ConfirmPassword',
}

const FieldText: Record<string, { text: string }> = {
  [FieldType.email]: {
    text: t('validation.emailInvalid'),
  },
  [FieldType.password]: {
    text: t('validation.passwordMinCharacters'),
  },
  [FieldType.confirmPassword]: {
    text: t('validation.passwordsDoNotMatch'),
  },
};

export const createRequiredValidator =
  <V>(label?: FieldType | string): FieldValidator<V> =>
  (value) => {
    if (value != null) {
      return undefined;
    }
    if (!label) {
      return t('validation.fieldRequired');
    }
    if (label in FieldText) {
      return FieldText[label].text;
    }

    return t('validation.fieldIsRequired', { fieldName: label });
  };

export function composeValidators<V>(...validators: FieldValidator<V>[]) {
  return (value: V, allValues: any) => {
    let error: string | undefined;
    validators.some((validator) => {
      error = validator(value, allValues);
      return error;
    });
    return error;
  };
}

export const strValidator = (value: string) => value.replace(/[^a-zA-Z ]+/g, '');

export const stripNonAsciiPassword = (password: string) => password.replace(/[^\x20-\x7E]/g, '');

export const emailValidator = (email: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const isValidEmail = String(email).toLowerCase().match(regex);
  return isValidEmail ? undefined : t('validation.emailInvalid');
};

export const validateEmailField = () => composeValidators(createRequiredValidator(t('common.email')), emailValidator);

const maxCharacterValidator = (max: number) => (value: string) => {
  if (value && value.length > max) {
    return t('validation.maxCharacter', { max });
  }
};

const validateNameField = (text?: string) =>
  composeValidators(createRequiredValidator(text), maxCharacterValidator(50));

export const validateFirstNameField = () => validateNameField();
export const validateLastNameField = () => validateNameField();

const ageValidator = (dateOfBirth: Date, legalAge: number): string | undefined => {
  const yearsPassed = dayjs().diff(dateOfBirth, 'year');

  let result: string | undefined = undefined;

  if (yearsPassed < 0) {
    result = t('validation.dateOfBirth.cannotBeInFuture');
  } else if (yearsPassed < legalAge) {
    result = t('validation.dateOfBirth.legalAge', { legalAge });
  } else if (yearsPassed > 100) {
    result = t('validation.dateOfBirth.invalidFormat');
  }

  return result;
};

export const validateAgeField = (territoryLegalAge: number) =>
  composeValidators(createRequiredValidator(), (value: Date) => ageValidator(value, territoryLegalAge));

export const validateStreetField = () =>
  composeValidators<string>(createRequiredValidator(), maxCharacterValidator(50));

export function validateZipCodeField(countryInfo: Country) {
  return composeValidators(createRequiredValidator(), (value: string) => {
    if (!postcodeValidator(value, countryInfo.code)) {
      return t('validation.fieldIsInvalid', { fieldName: countryInfo.postalCodeName });
    }
  });
}
